/**
 * FikoMalzemeKarsilastir Dialog — Malzeme/Maliyet/CO2 karşılaştırma UI
 * Modeli ölç → tüm malzemelerde ağırlık/maliyet/karbon tablosu + en ucuz/yeşil/hafif rozeti.
 */
import { Component, Input, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FikoMassInfo } from '../fiko-mass-info/FikoMassInfo';
import { malzemeKarsilastir, MalzemeSatir } from './FikoMalzemeKarsilastir';
import { baglamYaz } from '../fiko-uretim-baglam/UretimBaglam';

@Component({
  selector: 'fikoyt-malzeme-karsilastir',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule],
  template: `
  <div class="mk">
    <div class="mk-ust">
      <div class="mk-in"><label>Hacim cm³</label><input type="number" [(ngModel)]="hacimCm3" (ngModelChange)="hesapla()" min="0"></div>
      <div class="mk-in"><label>Fire %</label><input type="number" [(ngModel)]="fire" (ngModelChange)="hesapla()" min="0"></div>
      <button class="mk-mini" (click)="modeldenAl()">⊕ Modelden</button>
    </div>
    @if (satirlar.length) {
      <div class="mk-tablo">
        <div class="mk-th"><span>Malzeme</span><span>Ağırlık</span><span>Maliyet</span><span>CO₂</span></div>
        @for (s of satirlar; track s.ad) {
          <div class="mk-tr" [class.ucuz]="s.enUcuz">
            <span class="mk-ad">{{ s.ad }}
              @if (s.enUcuz) { <i class="rz ucuz" title="En ucuz">₺</i> }
              @if (s.enYesil) { <i class="rz yesil" title="En düşük karbon">🌱</i> }
              @if (s.enHafif) { <i class="rz hafif" title="En hafif">⚖</i> }
            </span>
            <span>{{ f(s.agirlikKg) }} kg</span>
            <span class="mk-mal">{{ f(s.maliyet, 0) }} ₺</span>
            <span class="mk-co2">{{ f(s.co2Kg, 1) }} kg</span>
          </div>
        }
      </div>
      <div class="mk-not">💡 En ucuz: <b>{{ enUcuz?.ad }}</b> · En yeşil: <b>{{ enYesil?.ad }}</b> · En hafif: <b>{{ enHafif?.ad }}</b></div>
    }
    @if (hata) { <div class="mk-hata">{{ hata }}</div> }
  </div>
  `,
  styles: [`
    .mk { padding:10px; min-width:300px; max-width:340px; font-size:11px; color:#e6f6ff;
      background:linear-gradient(180deg,rgba(0,40,60,.96),rgba(0,18,36,.98)); border-radius:6px; }
    .mk-ust { display:flex; gap:6px; align-items:flex-end; margin-bottom:8px; }
    .mk-in { display:flex; flex-direction:column; gap:2px; flex:1; }
    .mk-in label { font-size:9px; color:#5fd9f5; }
    .mk-in input { background:rgba(0,60,80,.8); border:1px solid rgba(0,200,220,.35); color:#fff; padding:4px; border-radius:4px; font-size:10px; outline:none; box-sizing:border-box; }
    .mk-mini { background:rgba(0,90,110,.6); border:1px solid rgba(0,200,220,.4); color:#bdf; border-radius:4px; padding:5px 8px; font-size:10px; cursor:pointer; white-space:nowrap; }
    .mk-mini:hover { background:rgba(0,130,160,.8); }
    .mk-tablo { border:1px solid rgba(0,200,220,.2); border-radius:5px; overflow:hidden; }
    .mk-th, .mk-tr { display:grid; grid-template-columns:1.6fr 1fr 1fr 0.9fr; gap:4px; padding:5px 7px; align-items:center; }
    .mk-th { background:rgba(0,90,110,.5); color:#bff; font-size:9px; }
    .mk-tr { border-top:1px solid rgba(0,200,220,.1); }
    .mk-tr.ucuz { background:rgba(0,200,120,.14); }
    .mk-ad { font-weight:600; } .mk-mal { color:#ffd76a; } .mk-co2 { color:#9be8a8; }
    .rz { font-style:normal; margin-left:3px; font-size:10px; }
    .mk-not { margin-top:8px; font-size:10px; color:#cfeaf5; line-height:1.5; } .mk-not b { color:#7dffc0; }
    .mk-hata { margin-top:8px; padding:6px; background:rgba(255,50,50,.2); border:1px solid rgba(255,100,100,.4); border-radius:4px; color:#ff9a9a; text-align:center; }
  `]
})
export class FikoMalzemeKarsilastirComponent {
  @Input() anaGosterim: any = null;
  @Input() pencere: any;
  @Input() ayarlar: any = {};
  @Input() modelleme: any = null;

  hacimCm3 = 240; fire = 10;
  satirlar: MalzemeSatir[] = [];
  hata: string | null = null;
  private mass = new FikoMassInfo();

  constructor(private cd: ChangeDetectorRef) { this.satirlar = malzemeKarsilastir(this.hacimCm3, this.fire); }

  get enUcuz() { return this.satirlar.find(s => s.enUcuz); }
  get enYesil() { return this.satirlar.find(s => s.enYesil); }
  get enHafif() { return this.satirlar.find(s => s.enHafif); }

  modeldenAl(): void {
    const sahne = this.anaGosterim?.sahneler?.[0];
    if (!sahne) { this.hata = 'Sahne yok'; this.cd.detectChanges(); return; }
    const r = this.mass.hesaplaModel(this.modelleme, sahne);   // aktif gövde (yoksa sahne)
    if (!r || r.ucgenSayisi === 0) { this.hata = 'Önce bir model oluşturun'; this.cd.detectChanges(); return; }
    this.hacimCm3 = +r.hacim.cm3.toFixed(1);
    const b = r.boundingBox.boyutlar;
    baglamYaz({ olcu: `${b.x.toFixed(0)}×${b.y.toFixed(0)}×${b.z.toFixed(0)}`, hacimCm3: this.hacimCm3, alanDm2: r.yuzeyAlani.cm2 / 100 });
    this.hata = null; this.hesapla();
  }

  hesapla(): void {
    try { this.satirlar = malzemeKarsilastir(this.hacimCm3, this.fire); } catch (e: any) { this.hata = e.message; }
    this.cd.detectChanges();
  }

  f(v: number, d = 2): string { return (v ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: d }); }
}
